import React, { useState, useEffect } from 'react';
import { 
  MdRocketLaunch, 
  MdHandshake, 
  MdTrendingUp, 
  MdGroups, 
  MdEco,
  MdAccessTime,
  MdVerified,
  MdAgriculture,
MdArrowForward,
MdPlayArrow,
MdAutoGraph,
MdWaterDrop,
MdLocalFlorist,
  MdShowChart,
  MdCheckCircle,
  MdNature,
  MdSmartToy,
  MdConnectWithoutContact,
  MdBalance,
  MdDiversity3
} from 'react-icons/md';
import { GiPlantWatering,
GiWheat,
GiGrowth, GiFarmer,  GiPlantRoots } from 'react-icons/gi';
import { FaLeaf,
FaChartLine, FaSeedling, FaGlobe,  FaRobot, FaArrowRight } from 'react-icons/fa';
import { RiLightbulbFlashLine, RiGlobalLine ,RiPlantLine
} from 'react-icons/ri';
import { Link } from 'react-router-dom';

const Vision = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);

    return () => {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);

  // Mission Points with SEO keywords
  const missionPoints = [
    {
      icon: <FaRobot />,
      title: 'Smart Robotics',
      text: 'Intelligent automation for modern farming',
      description: 'Deploying AI-powered robots that reduce labor by 70% and increase precision.',
      features: ['Automated Planting', 'Precision Harvesting', 'AI Monitoring'],
      progress: 85,
      keywords: 'agricultural robotics, smart farming robots, AI agriculture, automated farming'
    },
    {
      icon: <MdNature />,
      title: 'Eco-Systems',
      text: 'Sustainable agricultural ecosystems',
      description: 'Creating closed-loop systems that recycle resources and eliminate waste.',
      features: ['Water Recycling', 'Organic Waste Management', 'Biodiversity Support'],
      progress: 78,
      keywords: 'sustainable farming, eco agriculture, green farming, environmental farming'
    },
    {
      icon: <MdSmartToy />,
      title: 'IoT Integration',
      text: 'Smart farming with real-time monitoring',
      description: 'Network of sensors providing actionable insights for optimal crop health.',
      features: ['Soil Sensors', 'Climate Monitoring', 'Crop Health Analytics'],
      progress: 92,
      keywords: 'IoT farming, smart agriculture sensors, precision farming technology'
    },
    {
      icon: <MdConnectWithoutContact />,
      title: 'Direct Markets',
      text: 'Eliminating intermediaries, empowering farmers',
      description: 'Digital platforms connecting farmers directly with consumers and businesses.',
      features: ['Farm-to-Consumer', 'Digital Marketplaces', 'Transparent Pricing'],
      progress: 65,
      keywords: 'direct farmer markets, agricultural e-commerce, farm produce marketing'
    },
    {
      icon: <MdBalance />,
      title: 'Fair Economy',
      text: 'Equitable profit distribution systems',
      description: 'Blockchain-enabled transparent pricing ensuring fair farmer compensation.',
      features: ['Smart Contracts', 'Price Transparency', 'Direct Payments'],
      progress: 72,
      keywords: 'fair trade agriculture, farmer income, agricultural economy, price transparency'
    },
    {
      icon: <MdDiversity3 />,
      title: 'Community Growth',
      text: 'Building resilient rural communities',
      description: 'Training programs and infrastructure development for sustainable growth.',
      features: ['Skill Development', 'Infrastructure Support', 'Market Access'],
      progress: 88,
      keywords: 'rural development, farmer communities, agricultural training, rural empowerment'
    }
  ];

  const impactStats = [
    { 
      value: '70%+', 
      label: 'Labor Reduction',
      description: 'Through automation systems',
      icon: <MdAccessTime />,
      keywords: 'farming automation, agricultural efficiency, labor saving farming'
    },
    { 
      value: '45%+', 
      label: 'Profit Increase',
      description: 'Via direct market access',
      icon: <MdShowChart />,
      keywords: 'farmer income increase, agricultural profitability, farm revenue growth'
    },
    { 
      value: '1000+', 
      label: 'Farmers Empowered',
      description: 'Across India with our solutions',
      icon: <GiFarmer />,
      keywords: 'Indian farmers, agricultural empowerment, farming community support'
    },
    { 
      value: '98%', 
      label: 'Success Rate',
      description: 'Farmer satisfaction score',
      icon: <MdVerified />,
      keywords: 'farming success rate, agricultural satisfaction, farmer feedback'
    }
  ];

  const coreValues = [
    {
      icon: <RiLightbulbFlashLine />,
      title: 'Innovate with Heart',
      description: 'Technology solutions rooted in empathy and human-centered design.',
      keywords: 'agricultural innovation, empathetic technology, human-centered farming'
    },
    {
      icon: <MdVerified />,
      title: 'Purpose-Driven Tech',
      description: 'Every innovation addresses specific farmer challenges with measurable impact.',
      keywords: 'impact-driven agriculture, purpose technology, measurable farming solutions'
    },
    {
      icon: <GiPlantRoots />,
      title: 'Inclusive Prosperity',
      description: 'Economic growth that benefits every stakeholder in the agricultural chain.',
      keywords: 'inclusive agriculture, farm prosperity, agricultural value chain'
    },
    {
      icon: <MdHandshake />,
      title: 'Farmer Partnership',
      description: 'Working alongside farmers as equal partners in innovation.',
      keywords: 'farmer collaboration, agricultural partnerships, co-innovation farming'
    },
    {
      icon: <MdEco />,
      title: 'Sustainable Growth',
      description: 'Balancing technological advancement with environmental stewardship.',
      keywords: 'sustainable agriculture, green technology farming, environmental stewardship'
    },
    {
      icon: <MdGroups />,
      title: 'Community First',
      description: 'Building solutions that strengthen rural communities.',
      keywords: 'rural community development, agricultural communities, farm village support'
    }
  ];

  const roadmapItems = [
    {
      year: '2024',
      title: 'Digital Foundation',
      description: 'Establishing core digital infrastructure and pilot programs',
      milestones: [
        'AI-powered mobile platform',
        '500+ pilot farmers onboarded',
        'Basic automation deployment',
        'Real-time monitoring system'
      ],
      icon: <MdRocketLaunch />,
      keywords: 'agricultural digitalization, farming tech infrastructure, pilot farming programs'
    },
    {
      year: '2025',
      title: 'Tech Expansion',
      description: 'Scaling smart farming solutions across regions',
      milestones: [
        'Drone fleet deployment',
        'IoT sensor networks',
        'Predictive analytics launch',
        'Regional partnerships'
      ],
      icon: <MdTrendingUp />,
      keywords: 'farming technology expansion, agricultural drones, IoT farming networks'
    },
    {
      year: '2026',
      title: 'National Integration',
      description: 'Pan-India deployment and comprehensive ecosystem',
      milestones: [
        'Multi-state operations',
        'Farmer education platform',
        'Export quality certification',
        'R&D innovation center'
      ],
      icon: <RiGlobalLine />,
      keywords: 'national agriculture integration, pan-India farming, agricultural R&D'
    },
    {
      year: '2027',
      title: 'Global Leadership',
      description: 'International expansion and climate-smart solutions',
      milestones: [
        'Global technology transfer',
        'Carbon credit programs',
        'International market access',
        'Sustainable certification'
      ],
      icon: <FaGlobe />,
      keywords: 'global agricultural leadership, climate-smart farming, international farming tech'
    }
  ];

  return (
    <div className="premium-vision">
      {/* SEO Meta Tags and Structured Data */}
      <head>
        <title>Vision for Indian Agriculture | Krishidnya - Transforming Farming with Technology</title>
        <meta name="description" content="Krishidnya's vision for revolutionizing Indian agriculture with smart technology, sustainable practices, and farmer empowerment. Join our mission to transform farming." />
        <meta name="keywords" content="agricultural vision, future farming India, smart farming technology, sustainable agriculture vision, farmer empowerment, agricultural innovation roadmap" />
        <meta property="og:title" content="Krishidnya Vision - Future of Indian Agriculture" />
        <meta property="og:description" content="Transforming agriculture with technology-driven solutions for sustainable farming and farmer prosperity" />
        <link rel="canonical" href="/vision" />
      </head>

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "Krishidnya Agricultural Vision",
          "description": "Our vision for transforming Indian agriculture through technology, sustainability, and innovation",
          "keywords": "agricultural technology vision, sustainable farming future, smart agriculture India",
          "mainEntity": {
            "@type": "Organization",
            "name": "Krishidnya",
            "description": "Leading agricultural technology company with a vision for sustainable farming",
            "founder": "Dedicated to farmer prosperity and technological innovation"
          }
        })}
      </script>

      {/* Hero Section */}
      <header className="hero-section" role="banner">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <FaSeedling />
                <span>Our Vision for Agriculture</span>
              </div>
              
              <h1 className="hero-title">
                Reimagining Agriculture
                <span className="title-green">
                  Through Innovation
                </span>
              </h1>
              
              <p className="hero-description">
                Building India's first intelligent agricultural ecosystem 
                where technology, sustainability, and community prosperity 
                grow together in harmony.
              </p>
              
              <div className="hero-actions">
                <Link to="/contact" className="btn-primary" aria-label="Partner with Krishidnya for agricultural transformation">
                  <span>Partner With Us</span>
                  <FaArrowRight className="btn-icon" />
                </Link>
                <Link to="/services" className="btn-secondary" aria-label="Explore our agricultural solutions">
                  Explore Solutions
                </Link>
              </div>
            </div>
            
            <div className="hero-stats">
              <div className="stats-grid">
                {impactStats.map((stat, index) => (
                  <div key={index} className="stat-preview">
                    <div className="stat-preview-icon">
                      {stat.icon}
                    </div>
                    <div className="stat-preview-content">
                      <div className="stat-preview-number">{stat.value}</div>
                      <div className="stat-preview-label">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="hero-bottom">
          <div className="container">
            <div className="scroll-indicator">
              <span>Scroll to explore</span>
              <div className="scroll-line"></div>
            </div>
          </div>
        </div>
      </header>

      {/* Vision Statement */}
      <section className="vision-statement-section" aria-labelledby="vision-statement-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="vision-statement-heading">Our Vision Statement</h2>
            <p className="section-description">
              A transformed agricultural landscape where cutting-edge technology meets traditional wisdom, 
              creating sustainable prosperity for farmers and ensuring food security for generations.
            </p>
          </div>
          
          <div className="vision-statement-card">
            <div className="vision-content">
              <div className="vision-quote">
                "We envision a future where every farm thrives, every farmer prospers, 
                and sustainable agriculture nourishes our planet."
              </div>
              
              <div className="vision-metrics">
                <div className="vision-metric">
                  <div className="metric-number">1000+</div>
                  <div className="metric-label">Farmers to Empower</div>
                  <meta itemProp="description" content="Target to empower 10 million farmers across India with technology" />
                </div>
                <div className="vision-metric">
                  <div className="metric-number">50%</div>
                  <div className="metric-label">Efficiency Target</div>
                  <meta itemProp="description" content="Goal to increase farming efficiency by 50% through technology" />
                </div>
                <div className="vision-metric">
                  <div className="metric-number">100%</div>
                  <div className="metric-label">Sustainability Goal</div>
                  <meta itemProp="description" content="Commitment to 100% sustainable farming practices" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Pillars */}
      <section className="mission-section" aria-labelledby="mission-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="mission-heading">Our Mission Pillars</h2>
            <p className="section-description">
              Six pillars of innovation reshaping Indian agriculture with technology and sustainability
            </p>
          </div>
          
          <div className="solutions-grid">
            {missionPoints.map((point, index) => (
              <article 
                key={index} 
                className="solution-card"
                itemScope
                itemType="https://schema.org/Service"
              >
                <div className="solution-icon" aria-hidden="true">
                  {point.icon}
                </div>
                <div className="solution-content">
                  <h3 itemProp="name">{point.title}</h3>
                  <p itemProp="description">{point.description}</p>
                  <div className="solution-features">
                    {point.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <MdCheckCircle className="feature-check" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="progress-container">
                    <div className="progress-track">
                      <div 
                        className="progress-bar"
                        style={{ width: `${point.progress}%` }}
                        aria-valuenow={point.progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <div className="progress-text">{point.progress}% Complete</div>
                  </div>
                  <meta itemProp="keywords" content={point.keywords} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="values-section" aria-labelledby="values-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="values-heading">Our Guiding Principles</h2>
            <p className="section-description">
              The core values that drive our commitment to agricultural transformation
            </p>
          </div>
          
          <div className="values-grid">
            {coreValues.map((value, index) => (
              <article key={index} className="value-card">
                <div className="value-icon" aria-hidden="true">
                  {value.icon}
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
                <meta itemProp="keywords" content={value.keywords} />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="impact-section" aria-labelledby="impact-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="impact-heading">Measuring Our Impact</h2>
            <p className="section-description">
              Tracking tangible progress towards agricultural excellence
            </p>
          </div>
          
          <div className="impact-grid">
            {impactStats.map((stat, index) => (
              <div key={index} className="impact-card">
                <div className="impact-icon" aria-hidden="true">
                  {stat.icon}
                </div>
                <div className="impact-number">{stat.value}</div>
                <div className="impact-label">{stat.label}</div>
                <p className="impact-desc">{stat.description}</p>
                <meta itemProp="keywords" content={stat.keywords} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="roadmap-section" aria-labelledby="roadmap-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="roadmap-heading">Strategic Roadmap</h2>
            <p className="section-description">
              Our journey from vision to reality, one milestone at a time
            </p>
          </div>
          
          <div className="timeline">
            {roadmapItems.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <div className="marker-icon">{item.icon}</div>
                  <div className="marker-year">{item.year}</div>
                </div>
                <div className="timeline-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="milestones">
                    {item.milestones.map((milestone, idx) => (
                      <div key={idx} className="milestone">
                        <MdCheckCircle />
                        <span>{milestone}</span>
                      </div>
                    ))}
                  </div>
                  <meta itemProp="keywords" content={item.keywords} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta" aria-labelledby="cta-heading">
        <div className="container">
          <div className="cta-content">
            <h2 id="cta-heading">Join Our Agricultural Revolution</h2>
            <p>Together, let's cultivate a future where technology nurtures nature, 
            innovation empowers farmers, and sustainable growth feeds generations.</p>
            
            <div className="cta-actions">
              <Link to="/contact" className="btn-primary" aria-label="Partner with Krishidnya for agricultural innovation">
                Partner With Us
              </Link>
              <Link to="/careers" className="btn-secondary" aria-label="Join Krishidnya agricultural technology team">
                Join Our Team
              </Link>
            </div>
            
            <div className="cta-features">
              <div className="feature">
                <MdCheckCircle /> <span>Collaborative Partnerships</span>
              </div>
              <div className="feature">
                <MdCheckCircle /> <span>Innovation Programs</span>
              </div>
              <div className="feature">
                <MdCheckCircle /> <span>Community Initiatives</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Premium Design System - Same as Home Page */
        :root {
          --primary-black: #000000;
          --secondary-black: #1A1A1A;
          --medium-black: #333333;
          --light-black: #666666;
          --primary-green: #2E8B57;
          --secondary-green: #3A7D34;
          --light-green: #4CAF7A;
          --accent-green: #2D5A27;
          --white: #FFFFFF;
          --off-white: #FAFAFA;
          --light-gray: #F5F5F5;
          --medium-gray: #E8E8E8;
          --border-color: #E0E0E0;
          --text-primary: #000000;
          --text-secondary: #333333;
          --text-light: #666666;
          --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
          --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
          --radius-sm: 6px;
          --radius-md: 10px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --transition: all 0.3s ease;
          --header-height: ${isMobile ? '70px' : '80px'}; /* Header height variable */
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .premium-vision {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: var(--text-primary);
          background: var(--white);
          line-height: 1.6;
          overflow-x: hidden;
          scroll-behavior: smooth;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 ${isMobile ? '20px' : '24px'};
        }

        /* Typography - Same as Home */
        h1, h2, h3, h4 {
          font-family: 'Source Serif Pro', serif;
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: var(--primary-black);
        }

        h1 { 
          font-size: ${isMobile ? '2.25rem' : isTablet ? '3rem' : '3.5rem'};
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        h2 { 
          font-size: ${isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem'};
          margin-bottom: 1rem;
        }

        h3 { 
          font-size: ${isMobile ? '1.25rem' : isTablet ? '1.5rem' : '1.75rem'};
          margin-bottom: 0.75rem;
        }

        p { 
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-secondary);
          line-height: 1.7;
          font-weight: 400;
        }

        .section-description {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-light);
          margin-top: 0.5rem;
          font-weight: 400;
        }

        /* Buttons - Same as Home */
        .btn-primary, .btn-secondary {
          padding: ${isMobile ? '12px 24px' : '14px 32px'};
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          border-radius: var(--radius-md);
          text-decoration: none;
          transition: var(--transition);
          border: 2px solid transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          text-align: center;
          min-width: ${isMobile ? '140px' : 'auto'};
        }

        .btn-primary {
          background: var(--primary-black);
          color: var(--white);
        }

        .btn-primary:hover, .btn-primary:focus {
          background: var(--secondary-black);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
          outline: 2px solid var(--primary-green);
        }

        .btn-secondary {
          background: transparent;
          color: var(--primary-black);
          border: 2px solid var(--primary-black);
        }

        .btn-secondary:hover, .btn-secondary:focus {
          background: var(--primary-black);
          color: var(--white);
          transform: translateY(-2px);
          outline: 2px solid var(--primary-green);
        }

        .btn-icon {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          transition: transform 0.3s ease;
        }

        .btn-primary:hover .btn-icon, .btn-primary:focus .btn-icon {
          transform: translateX(4px);
        }

        /* Hero Section - Fixed Header Spacing */
        .hero-section {
          padding: ${isMobile ? 'calc(var(--header-height) + 40px) 0 40px' : 'calc(var(--header-height) + 60px) 0 60px'};
          background: var(--white);
          position: relative;
          margin-top: 0;
        }

        .hero-section::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border-color), transparent);
        }

        .hero-content {
          display: flex;
          flex-direction: ${isMobile ? 'column' : 'row'};
          gap: ${isMobile ? '40px' : '60px'};
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-text {
          flex: 1;
        }

        .hero-stats {
          flex: ${isMobile ? '0 0 auto' : '1'};
          width: ${isMobile ? '100%' : 'auto'};
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
          padding: ${isMobile ? '8px 16px' : '10px 20px'};
          border-radius: var(--radius-round);
          font-size: ${isMobile ? '0.85rem' : '14px'};
          font-weight: 600;
          margin-bottom: ${isMobile ? '20px' : '30px'};
          border: 1px solid rgba(46, 139, 87, 0.2);
        }

        .hero-badge svg {
          font-size: ${isMobile ? '0.9rem' : '16px'};
        }

        .hero-title {
          margin-bottom: ${isMobile ? '1rem' : '1.5rem'};
          line-height: 1.1;
        }

        .title-green {
          color: var(--primary-green);
          display: block;
          margin-top: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .hero-description {
          margin-bottom: ${isMobile ? '2rem' : '3rem'};
          font-size: ${isMobile ? '1.063rem' : '1.125rem'};
          line-height: 1.6;
        }

        .hero-actions {
          display: flex;
          gap: ${isMobile ? '0.75rem' : '1rem'};
          margin-bottom: ${isMobile ? '3rem' : '0'};
          flex-direction: ${isMobile ? 'column' : 'row'};
          align-items: ${isMobile ? 'stretch' : 'flex-start'};
        }

        /* Stats Preview - Same as Home */
        .stats-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr 1fr' : '1fr 1fr'};
          gap: ${isMobile ? '1rem' : '1.5rem'};
          background: var(--off-white);
          padding: ${isMobile ? '1.5rem' : '2rem'};
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-color);
        }

        .stat-preview {
          display: flex;
          align-items: center;
          gap: ${isMobile ? '0.75rem' : '1rem'};
        }

        .stat-preview-icon {
          width: ${isMobile ? '40px' : '48px'};
          height: ${isMobile ? '40px' : '48px'};
          background: var(--white);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: ${isMobile ? '1.25rem' : '1.5rem'};
          color: var(--primary-green);
          flex-shrink: 0;
          border: 1px solid var(--border-color);
        }

        .stat-preview-content {
          flex: 1;
        }

        .stat-preview-number {
          font-size: ${isMobile ? '1.5rem' : '1.75rem'};
          font-weight: 700;
          color: var(--primary-black);
          margin-bottom: 0.25rem;
          font-family: 'Source Serif Pro', serif;
        }

        .stat-preview-label {
          font-size: ${isMobile ? '0.75rem' : '0.875rem'};
          color: var(--text-light);
          line-height: 1.4;
          font-weight: 500;
        }

        /* Scroll Indicator */
        .hero-bottom {
          padding-top: ${isMobile ? '2rem' : '3rem'};
        }

        .scroll-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-light);
          font-size: 0.875rem;
        }

        .scroll-line {
          width: 1px;
          height: 40px;
          background: var(--border-color);
          position: relative;
        }

        .scroll-line::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 20px;
          background: var(--primary-green);
          animation: scroll 2s infinite;
        }

        /* Sections Common - Fixed Header Spacing */
        section {
          padding: ${isMobile ? '80px 0' : '120px 0'};
          scroll-margin-top: var(--header-height); /* Fixed header offset */
        }

        .section-intro {
          text-align: center;
          max-width: 700px;
          margin: 0 auto ${isMobile ? '2rem' : '3rem'};
        }

        /* Vision Statement Section */
        .vision-statement-section {
          background: var(--light-gray);
        }

        .vision-statement-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '2rem 1.5rem' : '3rem 2.5rem'};
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
          box-shadow: var(--shadow-md);
        }

        .vision-quote {
          font-family: 'Source Serif Pro', serif;
          font-size: ${isMobile ? '1.25rem' : '1.5rem'};
          font-style: italic;
          color: var(--text-primary);
          line-height: 1.6;
          margin-bottom: ${isMobile ? '2rem' : '3rem'};
          position: relative;
          padding: 0 ${isMobile ? '1rem' : '2rem'};
        }

        .vision-quote::before,
        .vision-quote::after {
          content: '"';
          font-family: serif;
          font-size: 3rem;
          color: var(--primary-green);
          opacity: 0.3;
          position: absolute;
        }

        .vision-quote::before {
          top: -10px;
          left: 0;
        }

        .vision-quote::after {
          bottom: -30px;
          right: 0;
        }

        .vision-metrics {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(3, 1fr)'};
          gap: ${isMobile ? '1.5rem' : '2rem'};
          padding-top: ${isMobile ? '1.5rem' : '2rem'};
          border-top: 1px solid var(--border-color);
        }

        .vision-metric {
          text-align: center;
        }

        .metric-number {
          font-size: ${isMobile ? '1.75rem' : '2rem'};
          font-weight: 700;
          color: var(--primary-green);
          margin-bottom: 0.5rem;
          font-family: 'Source Serif Pro', serif;
        }

        .metric-label {
          font-size: ${isMobile ? '0.875rem' : '1rem'};
          color: var(--text-secondary);
          font-weight: 500;
        }

        /* Mission Section */
        .mission-section {
          background: var(--white);
        }

        .solutions-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
          gap: ${isMobile ? '1.5rem' : '2rem'};
        }

        .solution-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem 1.25rem' : '2rem 1.75rem'};
          transition: var(--transition);
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .solution-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-green);
        }

        .solution-icon {
          width: ${isMobile ? '48px' : '56px'};
          height: ${isMobile ? '48px' : '56px'};
          background: linear-gradient(135deg, #2E8B57 0%, #3A7D34 100%);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: ${isMobile ? '1.25rem' : '1.5rem'};
          color: var(--white);
          margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
        }

        .solution-content {
          flex: 1;
        }

        .solution-content h3 {
          margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .solution-content p {
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          color: var(--text-light);
          margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
          line-height: 1.6;
        }

        .solution-features {
          margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
          font-size: ${isMobile ? '0.875rem' : '0.95rem'};
          color: var(--text-secondary);
        }

        .feature-check {
          color: var(--primary-green);
          flex-shrink: 0;
          font-size: ${isMobile ? '0.875rem' : '1rem'};
        }

        /* Progress Bar */
        .progress-container {
          margin-top: 1rem;
        }

        .progress-track {
          height: 6px;
          background: var(--border-color);
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 0.5rem;
        }

        .progress-bar {
          height: 100%;
          background: var(--primary-green);
          border-radius: 3px;
          transition: width 0.6s ease;
        }

        .progress-text {
          font-size: 0.75rem;
          color: var(--text-light);
          font-weight: 500;
          text-align: right;
        }

        /* Values Section */
        .values-section {
          background: var(--light-gray);
        }

        .values-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
          gap: ${isMobile ? '1.5rem' : '2rem'};
        }

        .value-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem 1.25rem' : '2rem 1.75rem'};
          text-align: center;
          transition: var(--transition);
        }

        .value-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .value-icon {
          font-size: ${isMobile ? '2rem' : '2.5rem'};
          color: var(--primary-green);
          margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
        }

        .value-card h3 {
          margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .value-card p {
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          color: var(--text-light);
          line-height: 1.6;
          margin: 0;
        }

        /* Impact Section */
        .impact-section {
          background: var(--white);
        }

        .impact-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr 1fr' : 'repeat(4, 1fr)'};
          gap: ${isMobile ? '1rem' : '1.5rem'};
        }

        .impact-card {
          background: var(--off-white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem 1rem' : '2rem 1.5rem'};
          text-align: center;
          transition: var(--transition);
        }

        .impact-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .impact-icon {
          font-size: ${isMobile ? '2rem' : '2.5rem'};
          color: var(--primary-green);
          margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
        }

        .impact-number {
          font-size: ${isMobile ? '1.75rem' : '2rem'};
          font-weight: 700;
          color: var(--primary-black);
          margin-bottom: 0.25rem;
          font-family: 'Source Serif Pro', serif;
        }

        .impact-label {
          font-size: ${isMobile ? '0.875rem' : '1rem'};
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 0.25rem;
        }

        .impact-desc {
          font-size: ${isMobile ? '0.75rem' : '0.875rem'};
          color: var(--text-light);
          margin: 0;
        }

        /* Roadmap Section */
        .roadmap-section {
          background: var(--light-gray);
        }

        .timeline {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          padding-left: ${isMobile ? '40px' : '50px'};
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: ${isMobile ? '15px' : '20px'};
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--border-color);
        }

        .timeline-item {
          position: relative;
          margin-bottom: ${isMobile ? '2rem' : '3rem'};
          padding-left: ${isMobile ? '20px' : '30px'};
        }

        .timeline-marker {
          position: absolute;
          left: -${isMobile ? '25px' : '30px'};
          width: ${isMobile ? '50px' : '60px'};
          height: ${isMobile ? '50px' : '60px'};
          background: var(--primary-green);
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--white);
          z-index: 1;
          border: 3px solid var(--white);
          box-shadow: var(--shadow-sm);
        }

        .marker-icon {
          font-size: ${isMobile ? '1.25rem' : '1.5rem'};
          margin-bottom: 4px;
        }

        .marker-year {
          font-size: ${isMobile ? '0.875rem' : '1rem'};
          font-weight: 600;
        }

        .timeline-content {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem' : '2rem'};
          box-shadow: var(--shadow-sm);
          transition: var(--transition);
        }

        .timeline-item:hover .timeline-content {
          transform: translateX(8px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .timeline-content h3 {
          margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .timeline-content p {
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          color: var(--text-light);
          margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
          line-height: 1.6;
        }

        .milestones {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .milestone {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: ${isMobile ? '0.875rem' : '0.95rem'};
          color: var(--text-secondary);
        }

        .milestone svg {
          color: var(--primary-green);
          flex-shrink: 0;
        }

        /* Final CTA */
        .final-cta {
          background: var(--white);
        }

        .cta-content {
          text-align: center;
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-content h2 {
          margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
        }

        .cta-content p {
          font-size: ${isMobile ? '1.063rem' : '1.125rem'};
          margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
          color: var(--text-secondary);
        }

        .cta-actions {
          display: flex;
          gap: ${isMobile ? '0.75rem' : '1rem'};
          justify-content: center;
          margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
          flex-direction: ${isMobile ? 'column' : 'row'};
          align-items: ${isMobile ? 'stretch' : 'center'};
        }

        .final-cta .btn-primary,
        .final-cta .btn-secondary {
          min-width: ${isMobile ? '100%' : '200px'};
        }

        .cta-features {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(3, 1fr)'};
          gap: ${isMobile ? '1rem' : '1.5rem'};
          justify-content: center;
        }

        .cta-features .feature {
          display: flex;
          align-items: center;
          justify-content: ${isMobile ? 'flex-start' : 'center'};
          gap: 0.5rem;
          color: var(--primary-green);
          font-weight: 500;
          font-size: ${isMobile ? '0.875rem' : '1rem'};
        }

        .cta-features .feature svg {
          color: var(--primary-green);
          flex-shrink: 0;
        }

        /* Animations */
        @keyframes scroll {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(20px);
            opacity: 0;
          }
        }

        /* Responsive Adjustments */
        @media (min-width: 769px) and (max-width: 1024px) {
          .hero-content {
            flex-direction: column;
          }
          
          .hero-stats {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
          }
          
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }
          
          .vision-metrics {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .timeline {
            padding-left: 60px;
          }
          
          .timeline::before {
            left: 30px;
          }
          
          .timeline-marker {
            left: -45px;
          }
        }

        @media (min-width: 1025px) {
          .hero-content {
            flex-direction: row;
          }
          
          .hero-stats {
            width: auto;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .impact-grid {
            grid-template-columns: repeat(4, 1fr);
          }
          
          .timeline {
            padding-left: 70px;
          }
          
          .timeline::before {
            left: 35px;
          }
          
          .timeline-marker {
            left: -55px;
          }
        }

        @media (max-width: 480px) {
          .vision-metrics {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .impact-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .timeline {
            padding-left: 30px;
          }
          
          .timeline::before {
            left: 10px;
          }
          
          .timeline-marker {
            left: -20px;
            width: 40px;
            height: 40px;
          }
          
          .marker-icon {
            font-size: 1rem;
          }
          
          .marker-year {
            font-size: 0.75rem;
          }
          
          .cta-features {
            grid-template-columns: 1fr;
            text-align: left;
          }
          
          section {
            padding: 60px 0;
          }
        }

        @media (max-width: 360px) {
          .timeline {
            padding-left: 20px;
          }
          
          .timeline::before {
            left: 5px;
          }
          
          .timeline-marker {
            left: -15px;
            width: 35px;
            height: 35px;
          }
          
          .hero-title {
            font-size: 1.75rem;
          }
          
          h2 {
            font-size: 1.5rem;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) and (pointer: coarse) {
          .solution-card:hover,
          .value-card:hover,
          .impact-card:hover,
          .timeline-item:hover .timeline-content {
            transform: none;
          }
          
          .btn-primary:hover,
          .btn-secondary:hover {
            transform: none;
          }
        }

        /* High Contrast Mode Support */
        @media (prefers-contrast: high) {
          .solution-card,
          .value-card,
          .impact-card,
          .timeline-content,
          .vision-statement-card {
            border: 2px solid var(--primary-black);
          }
          
          .progress-bar {
            border: 1px solid var(--primary-black);
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          
          .scroll-line::after {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Vision;
